import { useState } from 'react';
import RealEstatePageLayout from '../components/partials/RealEstatePageLayout';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import { useFormik } from 'formik';
import * as yup from 'yup';
import PhoneInputField from '../components/PhoneInputField';
import { PhoneNumberUtil } from 'google-libphonenumber';
import Swal from 'sweetalert2';
import axios from 'axios';
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';

const validationSchema = yup.object({
  first_name: yup.string().required('Le prénom est obligatoire'),
  last_name: yup.string().required('Le nom est obligatoire'),
  adresse: yup.string().required("L'adresse est obligatoire"),
  type_bien: yup.string().required('Le type de bien est obligatoire'),
  prix_location: yup.number().required('Le prix souhaité est obligatoire').positive("Le prix doit être positif"),
  superficie: yup.number().required('La superficie est obligatoire').positive("La superficie doit être positive"),
  phone_number: yup.string()
    .required("Le numéro de téléphone est obligatoire")
    .test('is-valid-phone', 'Numéro de téléphone invalide', function (value) {
      if (!value) return false;

      try {
        const phoneUtil = PhoneNumberUtil.getInstance();
        return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(value));
      } catch (error) {
        return false;
      }
    }),
  email: yup.string().email("L'email doit être valide").required("L'email est obligatoire"),
  commentaire: yup.string(),
});

const AddRental = () => {
  const { t } = useTranslation('common');

  const formik = useFormik({
    initialValues: {
      first_name: '',
      last_name: '',
      adresse: '',
      type_bien: '',
      prix_location: '',
      superficie: '',
      phone_number: '',
      email: '',
      commentaire: '',
    },
    validationSchema,
    onSubmit: values => {
      const formData = new FormData();
      Object.keys(values).forEach(key => {
        formData.append(key, values[key]);
      });

      axios.post(`${process.env.NEXT_PUBLIC_API_URL}/api/ads/leads/rental`, formData)
        .then(response => {
          formik.resetForm();
          Swal.fire({
            text: t("Votre demande de location a été soumise avec succès. Nous vous contacterons bientôt."),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: t("Ok, Merci!"),
            customClass: {
              confirmButton: 'btn btn-primary',
            },
          });
        })
        .catch(error => {
          Swal.fire({
            text: t("Une erreur s'est produite lors de la soumission de votre demande. Veuillez réessayer."),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: t("Réessayer"),
            customClass: {
              confirmButton: 'btn btn-danger',
            },
          });
        });
    },
  });

  return (
    <RealEstatePageLayout
      pageTitle={t("Mise en location")}
      activeNav="Pages"
      description={t("Confiez la mise en location de votre bien à Facilykey : recherche et sélection des locataires, visites et gestion du dossier.")}
    >
      <Container className="mt-5 mb-md-4 py-5">

        <Breadcrumb className="mb-3 pt-md-3">
          <Breadcrumb.Item linkAs={Link} href="/">{t("Accueil")}</Breadcrumb.Item>
          <Breadcrumb.Item active>{t("Mise en location")}</Breadcrumb.Item>
        </Breadcrumb>

        <h1 className="h2 mb-4">{t("Mise en location")}</h1>

        <Form onSubmit={formik.handleSubmit}>
          <Row>
            <Form.Group as={Col} sm={6} className="mb-3">
              <Form.Label>{t("Prénom")}</Form.Label>
              <Form.Control
                name="first_name"
                value={formik.values.first_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.first_name && formik.errors.first_name}
              />
              <Form.Control.Feedback type="invalid">{t(formik.errors.first_name)}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group as={Col} sm={6} className="mb-3">
              <Form.Label>{t("Nom")}</Form.Label>
              <Form.Control
                name="last_name"
                value={formik.values.last_name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.last_name && formik.errors.last_name}
              />
              <Form.Control.Feedback type="invalid">{t(formik.errors.last_name)}</Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>{t("Adresse du bien")}</Form.Label>
            <Form.Control
              name="adresse"
              value={formik.values.adresse}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.adresse && formik.errors.adresse}
            />
            <Form.Control.Feedback type="invalid">{t(formik.errors.adresse)}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t("Type de bien")}</Form.Label>
            <Form.Control
              as="select"
              name="type_bien"
              value={formik.values.type_bien}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.type_bien && formik.errors.type_bien}
            >
              <option value="" disabled>
                {t("Choisissez le type de bien")}
              </option>
              <option value="Appartement">{t("Appartement")}</option>
              <option value="Maison">{t("Maison")}</option>
              <option value="Villa">{t("Villa")}</option>
              <option value="Immeuble">{t("Immeuble")}</option>
              <option value="Terrain">{t("Terrain")}</option>
              <option value="Parking">{t("Parking")}</option>
              <option value="Local commercial">{t("Local commercial")}</option>
            </Form.Control>
            <Form.Control.Feedback type="invalid">{t(formik.errors.type_bien)}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t("Prix souhaité en location (MAD)")}</Form.Label>
            <Form.Control
              type="number"
              name="prix_location"
              value={formik.values.prix_location}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.prix_location && formik.errors.prix_location}
            />
            <Form.Control.Feedback type="invalid">{t(formik.errors.prix_location)}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t("Superficie (m²)")}</Form.Label>
            <Form.Control
              type="number"
              name="superficie"
              value={formik.values.superficie}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.superficie && formik.errors.superficie}
            />
            <Form.Control.Feedback type="invalid">{t(formik.errors.superficie)}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t("Numéro de téléphone")}</Form.Label>
            <PhoneInputField formik={formik} />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t("Adresse email")}</Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.email && formik.errors.email}
            />
            <Form.Control.Feedback type="invalid">{t(formik.errors.email)}</Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t("Commentaire")}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="commentaire"
              value={formik.values.commentaire}
              onChange={formik.handleChange}
            />
          </Form.Group>

          <button type="submit" className="btn btn-primary">
            {t("Envoyer")}
          </button>
        </Form>

      </Container>
    </RealEstatePageLayout>
  );
};

export default AddRental;
